<script setup lang="ts">
import type { Tone } from '@/lib/tone'
import { categoryTitle } from '@/lib/tone'
import ToneGlyph from './ToneGlyph.vue'

interface Section {
  readonly category: string
  readonly anchor: string
  readonly failed: number
  readonly unknown: number
  readonly total: number
}

const props = defineProps<{
  sections: readonly Section[]
  activeAnchor: string
}>()

const emit = defineEmits<{
  (e: 'jump', anchor: string): void
}>()

// Failed outranks unknown, which outranks clean. That is the order a reader
// cares about a section in, not the order fields happen to sit in
// `CategoryCount`.
function toneFor(section: Section): Tone {
  if (section.failed > 0) return 'high'
  if (section.unknown > 0) return 'unknown'
  return 'pass'
}

function countFor(section: Section): number | null {
  if (section.failed > 0) return section.failed
  if (section.unknown > 0) return section.unknown
  return null
}
</script>

<template>
  <nav aria-label="Report sections">
    <ul class="flex snap-x gap-1 overflow-x-auto lg:flex-col lg:snap-none lg:overflow-visible">
      <li
        v-for="section in props.sections"
        :key="section.anchor"
        class="snap-start"
      >
        <button
          type="button"
          class="tap flex w-full items-center justify-between gap-3 rounded-md px-2.5 text-left text-sm whitespace-nowrap transition-colors duration-100 hover:bg-sunken"
          :class="
            section.anchor === props.activeAnchor
              ? 'bg-accent-soft font-semibold text-ink'
              : 'font-normal text-ink-muted'
          "
          :aria-current="section.anchor === props.activeAnchor ? 'true' : undefined"
          @click="emit('jump', section.anchor)"
        >
          <span>{{ categoryTitle(section.category) }}</span>
          <span
            class="inline-flex items-center gap-1.5"
            :data-tone="toneFor(section)"
          >
            <ToneGlyph
              v-if="countFor(section) === null"
              glyph="check"
            />
            <span
              v-else
              class="tone-dot"
              aria-hidden="true"
            />
            <span
              v-if="countFor(section) !== null"
              class="tabular text-xs"
            >{{ countFor(section) }}</span>
          </span>
        </button>
      </li>
    </ul>
  </nav>
</template>

<style scoped>
.tone-dot {
  display: inline-block;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 9999px;
  background: var(--tone-accent);
}
</style>
